import {Injectable} from '@angular/core';
import {Action, createSelector, State, StateContext} from '@ngxs/store';
import {patch} from '@ngxs/store/operators';
import {JsonApiResponse, pluckData} from '@tisa-group/cdk';
import {
  OttClientPlaylistService,
  OttEventService,
  OttPlaylistService,
} from '@tisa-group/core/api/umpire';
import {UmpireOttEvent} from '@tisa-group/models';
import {Observable, tap} from 'rxjs';
import {LoadPlaylistEvents} from './playlist-events.actions';

interface PaginatedItemsModel {
  items: UmpireOttEvent[];
  totalItems: number;
  currentPage: number;
}

const PAGE_SIZE = 18;

export type PlaylistEventsStateModel = Record<string, PaginatedItemsModel>;

@State<PlaylistEventsStateModel>({
  name: 'PlaylistEvents',
  defaults: undefined,
})
@Injectable()
export class PlaylistEventsState {
  @Action(LoadPlaylistEvents)
  LoadPlaylistEvents(
    ctx: StateContext<PlaylistEventsStateModel>,
    action: LoadPlaylistEvents,
  ): Observable<UmpireOttEvent[]> | UmpireOttEvent[] {
    const {playlistSlug} = action;

    const playlistState = ctx.getState()[playlistSlug];

    // Pagination done, no more items to fetch
    if (
      playlistState?.totalItems &&
      playlistState?.items?.length >= playlistState.totalItems
    ) {
      return playlistState.items;
    }

    return this.playlistService
      .fetchEvents(playlistSlug, {
        include: ['image_horizontal', 'image_vertical', 'format'].join(','),
        sort: ['custom_events_sort'],
        page: {
          size: PAGE_SIZE,
          number: playlistState?.currentPage || 0,
        },
      })
      .pipe(
        tap(res =>
          ctx.setState(
            patch({
              [playlistSlug]: {
                items: [...(playlistState?.items ?? []), ...res.data],
                currentPage: res.meta.pagination.current + 1,
                totalItems: res.meta.pagination.records,
              },
            }),
          ),
        ),
        pluckData(),
      );
  }

  static getPlaylistEvents(
    playlistSlug: string,
  ): (state: PlaylistEventsStateModel) => UmpireOttEvent[] {
    return createSelector<(state: PlaylistEventsStateModel) => UmpireOttEvent[]>(
      [PlaylistEventsState],
      (state: PlaylistEventsStateModel) => {
        return state[playlistSlug]?.items;
      },
    );
  }

  constructor(readonly playlistService: OttClientPlaylistService) {}
}
